// Mock data for the educational platform - Updated per new mindmap

export const ageGroups = [
  {
    id: '6-8',
    label: 'Ages 6-8',
    name: 'Early & Emerging Learners',
    description: 'Group A: Play-based learning with visuals and movement',
    characteristics: [
      'Very play-based',
      'Short attention span',
      'Concrete thinking',
      'Understand meaning before producing language'
    ],
    learningFocus: [
      'Listening & speaking',
      'Sounds, rhythm, repetition',
      'Visuals + movement'
    ],
    teachingMethods: [
      'Songs & chants 🎵',
      'Storytelling with pictures',
      'Role-play & movement games',
      'Flashcards + physical actions',
      'TPR (Simon Says, act-it-out games)'
    ],
    icon: '👶',
    uiComplexity: 'simple', // Minimal text, more visuals
    attentionSpan: 'short' // 5-10 minutes
  },
  {
    id: '9-11',
    label: 'Ages 9-11',
    name: 'Expanding Thinkers & Pre-Teens',
    description: 'Group B: Logical thinking with rules and challenges',
    characteristics: [
      'More logical and reflective',
      'Can follow rules and multi-step instructions',
      'Enjoy challenges and competition',
      'Begin noticing grammar patterns'
    ],
    learningFocus: [
      'Reading short texts independently',
      'Purposeful speaking',
      'Early writing & structure',
      'Problem-solving'
    ],
    teachingMethods: [
      'Projects (posters, mini-presentations)',
      'Games with rules',
      'Story sequencing & story cubes',
      'Scenario-based role-play',
      'Simple creative writing'
    ],
    icon: '🧠',
    uiComplexity: 'detailed', // More text, instructions
    attentionSpan: 'longer' // 15-20 minutes
  }
];

export const learningModules = [
  {
    id: 'vocabulary-sounds',
    name: 'Vocabulary & Sounds',
    description: 'Alphabet, vocabulary, letter sounds, and guided writing',
    icon: '🔤',
    color: '#FF6B6B',
    games: ['Flashcards', 'Bingo', 'Mystery Bag/Box', 'Memory/Matching'],
    availableFor: ['6-8', '9-11']
  },
  {
    id: 'reading-comprehension',
    name: 'Reading & Comprehension',
    description: 'Short readable texts with visual support (primarily 9-11)',
    icon: '📚',
    color: '#FFE66D',
    games: ['Read/Listen and Act', 'Find the Clues', 'Sequencing Activities'],
    availableFor: ['9-11'] // Primarily for older group
  },
  {
    id: 'storytelling',
    name: 'Storytelling with Pictures',
    description: 'Learn through stories, visuals, and sequencing',
    icon: '📖',
    color: '#A8E6CF',
    games: ['Picture Stories', 'Story Sequencing', 'Story Cubes', 'Picture Story Creation'],
    availableFor: ['6-8', '9-11']
  },
  {
    id: 'conversation',
    name: 'Conversation & Small Talk',
    description: 'Build confidence in real-life communication',
    icon: '💬',
    color: '#FFB3C0',
    games: ['Guided Small-talk', 'Role-play Dialogues', 'Choice-based Responses'],
    availableFor: ['6-8', '9-11']
  },
  {
    id: 'listening-speaking',
    name: 'Listening & Speaking',
    description: 'Comprehension before production, pronunciation through repetition',
    icon: '👂',
    color: '#4ECDC4',
    games: ['Simon Says (TPR)', 'Telephone/Whisper', 'Role-play scenarios', 'Listen & Act'],
    availableFor: ['6-8', '9-11']
  }
];

export const mockActivities = {
  '6-8': {
    'vocabulary-sounds': [
      { 
        id: 1, 
        name: 'Animal Flashcards', 
        type: 'flashcards', 
        duration: 5, 
        difficulty: 'easy',
        description: 'Learn animal names with fun flashcards',
        pedagogicalNote: 'Visual + movement based'
      },
      { 
        id: 2, 
        name: 'Color Matching', 
        type: 'matching', 
        duration: 6, 
        difficulty: 'easy',
        description: 'Match colors with pictures',
        pedagogicalNote: 'Concrete thinking, visual support'
      },
      { 
        id: 3, 
        name: 'Mystery Bag', 
        type: 'mystery-bag', 
        duration: 7, 
        difficulty: 'easy',
        description: 'Guess what\'s in the bag!',
        pedagogicalNote: 'Play-based, hands-on'
      },
      { 
        id: 4, 
        name: 'Letter Sounds', 
        type: 'letter-sounds', 
        duration: 8, 
        difficulty: 'easy',
        description: 'Learn letter sounds with actions',
        pedagogicalNote: 'Sounds, rhythm, repetition'
      }
    ],
    'listening-speaking': [
      { 
        id: 5, 
        name: 'Simon Says', 
        type: 'simon-says', 
        duration: 8, 
        difficulty: 'easy',
        description: 'Follow instructions with actions',
        pedagogicalNote: 'TPR, movement-based'
      },
      { 
        id: 6, 
        name: 'Songs & Chants', 
        type: 'songs', 
        duration: 7, 
        difficulty: 'easy',
        description: 'Learn through music and rhythm',
        pedagogicalNote: 'Rhythm, repetition, fun'
      },
      { 
        id: 7, 
        name: 'Listen & Act', 
        type: 'listen-act', 
        duration: 6, 
        difficulty: 'easy',
        description: 'Listen and do the action',
        pedagogicalNote: 'Comprehension before production'
      },
      { 
        id: 8, 
        name: 'Role-play Scenarios', 
        type: 'role-play', 
        duration: 10, 
        difficulty: 'easy',
        description: 'Act out simple scenarios',
        pedagogicalNote: 'Visuals + movement'
      }
    ],
    'storytelling': [],
    'conversation': [
      { 
        id: 11, 
        name: 'Small Talk Practice', 
        type: 'small-talk', 
        duration: 7, 
        difficulty: 'easy',
        description: 'Practice simple conversations',
        pedagogicalNote: 'Guided prompts, confidence building'
      }
    ]
  },
  '9-11': {
    'vocabulary-sounds': [
      { 
        id: 12, 
        name: 'Word Bingo', 
        type: 'bingo', 
        duration: 12, 
        difficulty: 'medium',
        description: 'Play bingo with vocabulary words',
        pedagogicalNote: 'Games with rules'
      },
      { 
        id: 13, 
        name: 'Memory Challenge', 
        type: 'memory', 
        duration: 15, 
        difficulty: 'medium',
        description: 'Match words and meanings',
        pedagogicalNote: 'Logical thinking, patterns'
      }
    ],
    'listening-speaking': [
      { 
        id: 14, 
        name: 'Telephone Game', 
        type: 'telephone', 
        duration: 12, 
        difficulty: 'medium',
        description: 'Pass the message along',
        pedagogicalNote: 'Multi-step instructions'
      },
      { 
        id: 15, 
        name: 'Scenario Role-play', 
        type: 'scenario-roleplay', 
        duration: 15, 
        difficulty: 'medium',
        description: 'Act out complex scenarios',
        pedagogicalNote: 'Purposeful speaking, problem-solving'
      }
    ],
    'reading-comprehension': [
      { 
        id: 16, 
        name: 'Read & Act', 
        type: 'read-act', 
        duration: 18, 
        difficulty: 'medium',
        description: 'Read short texts and act them out',
        pedagogicalNote: 'Reading before production'
      },
      { 
        id: 17, 
        name: 'Find the Clues', 
        type: 'find-clues', 
        duration: 20, 
        difficulty: 'medium',
        description: 'Read and solve the mystery',
        pedagogicalNote: 'Problem-solving, comprehension'
      },
      { 
        id: 18, 
        name: 'Text Sequencing', 
        type: 'text-sequencing', 
        duration: 15, 
        difficulty: 'medium',
        description: 'Put story paragraphs in order',
        pedagogicalNote: 'Reading independently, structure'
      }
    ],
    'storytelling': [
      { 
        id: 19, 
        name: 'Story Cubes', 
        type: 'story-cubes', 
        duration: 20, 
        difficulty: 'medium',
        description: 'Create stories with story cubes',
        pedagogicalNote: 'Creative thinking, structure'
      },
      { 
        id: 20, 
        name: 'Picture Story Creation', 
        type: 'picture-creation', 
        duration: 18, 
        difficulty: 'medium',
        description: 'Create your own picture story',
        pedagogicalNote: 'Early writing, creative expression'
      },
      { 
        id: 21, 
        name: 'Story Sequencing Challenge', 
        type: 'story-challenge', 
        duration: 22, 
        difficulty: 'medium',
        description: 'Advanced story sequencing',
        pedagogicalNote: 'Multi-step, logical thinking'
      }
    ],
    'conversation': [
      { 
        id: 22, 
        name: 'Guided Dialogues', 
        type: 'guided-dialogues', 
        duration: 15, 
        difficulty: 'medium',
        description: 'Practice structured conversations',
        pedagogicalNote: 'Real-life communication, confidence'
      },
      { 
        id: 23, 
        name: 'Choice-based Responses', 
        type: 'choice-responses', 
        duration: 12, 
        difficulty: 'medium',
        description: 'Choose how to respond in conversations',
        pedagogicalNote: 'Purposeful speaking, decision-making'
      }
    ]
  }
};

export const mockProgress = {
  totalStars: 45,
  completedActivities: 12,
  totalActivities: 50,
  badges: ['First Steps', 'Word Master', 'Story Teller', 'Conversation Starter'],
  currentStreak: 3,
  ageGroup: null // Will be set when user selects
};